import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Leaf, Package, Scale, Sprout } from 'lucide-react'

import type { PublicTraceCareLog } from '@/lib/api/trace-codes-api'
import { fetchPublicTrace } from '@/lib/api/trace-codes-api'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/trace/$code')({
    component: TracePublicPage,
})

const activityLabel: Record<string, () => string> = {
    fertilizing: m.care_log_activity_fertilizing,
    harvesting: m.care_log_activity_harvesting,
    replanting: m.care_log_activity_replanting,
    spraying: m.care_log_activity_spraying,
    weeding: m.care_log_activity_weeding,
    watering: m.care_log_activity_watering,
    pruning: m.care_log_activity_pruning,
    bud_picking: m.care_log_activity_bud_picking,
    other: m.care_log_activity_other,
}

function formatDate(iso: string) {
    return iso.slice(0, 10)
}

function CareLogItem({ log }: { log: PublicTraceCareLog }) {
    const label = activityLabel[log.activity_type]?.() ?? log.activity_type
    return (
        <div className="border-l-2 border-green-200 pl-4 py-1 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium">
                <Leaf className="size-4 text-green-600" />
                <span>{label}</span>
                <span className="text-muted-foreground font-normal">
                    — {formatDate(log.log_date)}
                </span>
            </div>
            {log.quantity > 0 && (
                <p className="text-sm text-muted-foreground">
                    {m.trace_care_log_quantity()}: {log.quantity}
                </p>
            )}
            {(log.fertilizers ?? []).length > 0 && (
                <p className="text-sm text-muted-foreground">
                    {m.trace_care_log_fertilizers()}:{' '}
                    {log.fertilizers!.join(', ')}
                </p>
            )}
            {(log.pesticides ?? []).length > 0 && (
                <p className="text-sm text-muted-foreground">
                    {m.trace_care_log_pesticides()}:{' '}
                    {log.pesticides!.join(', ')}
                </p>
            )}
            {log.quarantine_note && (
                <p className="text-sm text-amber-700">
                    {m.trace_care_log_quarantine_note()}: {log.quarantine_note}
                </p>
            )}
            {(log.parcels ?? []).length > 0 && (
                <p className="text-sm text-muted-foreground">
                    {m.trace_care_log_parcels()}:{' '}
                    {log.parcels!.map((p) => p.name).join(', ')}
                </p>
            )}
        </div>
    )
}

function TracePublicPage() {
    const { code } = Route.useParams()

    const { data, isLoading, isError } = useQuery({
        queryKey: ['public-trace', code],
        queryFn: () => fetchPublicTrace(code),
        retry: false,
    })

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <p className="text-muted-foreground animate-pulse">
                    {m.trace_loading()}
                </p>
            </div>
        )
    }

    if (isError || !data) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-4 px-4">
                <AlertCircle className="size-12 text-destructive" />
                <h1 className="text-xl font-semibold text-destructive">
                    {m.trace_not_found_title()}
                </h1>
                <p className="text-muted-foreground text-center max-w-xs">
                    {m.trace_not_found_desc()}
                </p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4">
            <div className="max-w-lg mx-auto space-y-6">
                {/* Header */}
                <div className="text-center space-y-1">
                    <div className="flex items-center justify-center gap-2 text-green-700">
                        <Sprout className="size-8" />
                    </div>
                    <h1 className="text-2xl font-bold text-green-800">
                        {m.trace_title()}
                    </h1>
                    <p className="text-xs text-muted-foreground font-mono">
                        {code}
                    </p>
                </div>

                {/* external_ref fallback */}
                {data.external_ref && (
                    <Card>
                        <p className="text-sm">{data.external_ref}</p>
                    </Card>
                )}

                {/* Grower info */}
                {data.grower && (
                    <Card title={m.trace_grower_title()}>
                        <p className="font-medium">{data.grower.name}</p>
                        {(data.grower.areas ?? []).length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {data.grower.areas!.map((area, i) => (
                                    <li
                                        key={i}
                                        className="text-sm text-muted-foreground flex gap-2"
                                    >
                                        <span className="font-medium text-foreground">
                                            {area.name}
                                        </span>
                                        {area.acreage ? (
                                            <span>— {area.acreage} ha</span>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                )}

                {/* Tea lot */}
                {data.tea_lot && (
                    <Card title={m.trace_tea_lot_title()}>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <LabelValue
                                label={m.trace_tea_lot_date()}
                                value={formatDate(data.tea_lot.lot_date)}
                            />
                            <LabelValue
                                label={m.trace_tea_lot_quantity()}
                                value={`${data.tea_lot.quantity} kg`}
                            />
                            {data.tea_lot.status && (
                                <LabelValue
                                    label={m.trace_tea_lot_status()}
                                    value={data.tea_lot.status}
                                />
                            )}
                        </div>
                    </Card>
                )}

                {/* Weighing */}
                {data.weighing && (
                    <Card
                        title={m.trace_weighing_title()}
                        icon={<Scale className="size-4" />}
                    >
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <LabelValue
                                label={m.trace_weighing_date()}
                                value={formatDate(data.weighing.weigh_date)}
                            />
                            <LabelValue
                                label={m.trace_weighing_quantity()}
                                value={`${data.weighing.quantity} kg`}
                            />
                            {data.weighing.tea_type && (
                                <LabelValue
                                    label={m.trace_weighing_tea_type()}
                                    value={data.weighing.tea_type}
                                />
                            )}
                        </div>
                    </Card>
                )}

                {/* Care logs */}
                {(data.care_logs ?? []).length > 0 && (
                    <Card title={m.trace_care_logs_title()}>
                        <div className="space-y-3">
                            {data.care_logs!.map((log, i) => (
                                <CareLogItem key={i} log={log} />
                            ))}
                        </div>
                    </Card>
                )}

                {/* Classifications */}
                {(data.classifications ?? []).length > 0 && (
                    <Card title={m.trace_classifications_title()}>
                        <div className="space-y-2">
                            {data.classifications!.map((c, i) => (
                                <div
                                    key={i}
                                    className="text-sm flex flex-wrap gap-3"
                                >
                                    <span className="font-medium">
                                        {c.name}
                                    </span>
                                    {c.code && (
                                        <span className="text-muted-foreground font-mono">
                                            {c.code}
                                        </span>
                                    )}
                                    <span className="text-muted-foreground">
                                        {c.weight} kg
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Aromas */}
                {(data.aromas ?? []).length > 0 && (
                    <Card title={m.trace_aromas_title()}>
                        <div className="space-y-2">
                            {data.aromas!.map((a, i) => (
                                <div key={i} className="text-sm space-y-0.5">
                                    <div className="flex flex-wrap gap-3">
                                        <span>
                                            {formatDate(a.sampling_date)}
                                        </span>
                                        {a.code && (
                                            <span className="font-mono text-muted-foreground">
                                                {a.code}
                                            </span>
                                        )}
                                        <span className="text-muted-foreground">
                                            {a.weight} kg
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Packages */}
                {(data.packages ?? []).length > 0 && (
                    <Card
                        title={m.trace_packages_title()}
                        icon={<Package className="size-4" />}
                    >
                        <div className="space-y-2">
                            {data.packages!.map((p, i) => (
                                <div
                                    key={i}
                                    className="text-sm flex flex-wrap gap-3"
                                >
                                    <span>{formatDate(p.package_date)}</span>
                                    <span className="text-muted-foreground">
                                        {p.weight} kg
                                    </span>
                                    {p.is_approved === true && (
                                        <span className="text-green-700 text-xs">
                                            {m.trace_package_approved()}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                <p className="text-center text-xs text-muted-foreground pb-4">
                    {m.trace_footer()}
                </p>
            </div>
        </div>
    )
}

// Internal layout helpers — not exported
function Card({
    title,
    icon,
    children,
}: {
    title?: string
    icon?: React.ReactNode
    children: React.ReactNode
}) {
    return (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
            {title && (
                <div className="flex items-center gap-2 text-green-800 font-semibold text-sm uppercase tracking-wide">
                    {icon}
                    <span>{title}</span>
                </div>
            )}
            {children}
        </div>
    )
}

function LabelValue({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="font-medium">{value}</p>
        </div>
    )
}
